/**
 * GridStack ↔ scorer layout schema adapter (builder-only).
 */

import {
  SCORER_LAYOUT_FRAME_PRESETS,
  CUSTOM_LAYOUT_GRID_MARGIN_PX,
} from './scorer-layout-schema.js';
import {
  getScorerWidgetDef,
  getScorerWidgetPaletteDef,
} from './scorer-layout-widget-catalog.js';

export const GRIDSTACK_WIDGET_META_KEY = 'scorerWidget';

/** Must match `margin` in profileToGridStackOptions — builder overlay gap uses the same value. */
export const GRIDSTACK_BUILDER_MARGIN = CUSTOM_LAYOUT_GRID_MARGIN_PX;

/** Schema col/row (1-based) → GridStack x/y (0-based). */
export function schemaToGridStackXY(col, row) {
  return {
    x: Math.max(0, (Number(col) || 1) - 1),
    y: Math.max(0, (Number(row) || 1) - 1),
  };
}

/** GridStack x/y (0-based) → schema col/row (1-based). */
export function gridStackToSchemaXY(x, y) {
  return {
    col: Math.max(1, (Number(x) || 0) + 1),
    row: Math.max(1, (Number(y) || 0) + 1),
  };
}

export function computeBuilderCellHeight(profile, profileKey = 'tabletLandscape') {
  const frame = profile?.frame || SCORER_LAYOUT_FRAME_PRESETS[profileKey];
  const rows = Math.max(1, Number(profile?.grid?.rows) || 1);
  const safeHeight = Number(frame?.safeHeight) || 720;
  // GridStack stacks row slots back-to-back; margin is applied inside each slot.
  return Math.max(32, Math.floor(safeHeight / rows));
}

export function computeBuilderGridMetrics(profile, profileKey = 'tabletLandscape', scale = 1) {
  const grid = profile?.grid || {};
  const cols = Math.max(1, Number(grid.columns) || 12);
  const rows = Math.max(1, Number(grid.rows) || 8);
  const margin = GRIDSTACK_BUILDER_MARGIN;
  const size = computeBuilderFrameSize(profile, profileKey, scale);
  const cellHeight = Math.max(16, Math.floor(computeBuilderCellHeight(profile, profileKey) * scale));
  const cellWidth = size.width / cols;
  const height = rows * cellHeight;
  return {
    cols,
    rows,
    margin,
    cellHeight,
    cellWidth,
    width: size.width,
    height,
  };
}

export function computeBuilderFrameSize(profile, profileKey = 'tabletLandscape', scale = 1) {
  const frame = profile?.frame || SCORER_LAYOUT_FRAME_PRESETS[profileKey];
  const safeWidth = Number(frame?.safeWidth) || 1200;
  const safeHeight = Number(frame?.safeHeight) || 720;
  const s = Number(scale) || 1;
  return {
    width: Math.round(safeWidth * s),
    height: Math.round(safeHeight * s),
    safeWidth,
    safeHeight,
  };
}

export function buildTrackOverlayStyle(profile, profileKey = 'tabletLandscape', scale = 1) {
  const metrics = computeBuilderGridMetrics(profile, profileKey, scale);
  return {
    width: `${metrics.width}px`,
    height: `${metrics.height}px`,
    maxWidth: '100%',
  };
}

/** Pixel box for one overlay cell — matches GridStack content inset (margin inside slot). */
export function buildTrackOverlayCellStyle(metrics, x = 0, y = 0) {
  const cellWidth = Number(metrics?.cellWidth) || 0;
  const cellHeight = Number(metrics?.cellHeight) || 0;
  const margin = Number(metrics?.margin) || GRIDSTACK_BUILDER_MARGIN;
  const slotX = Math.max(0, Number(x) || 0);
  const slotY = Math.max(0, Number(y) || 0);
  return {
    left: `${slotX * cellWidth + margin}px`,
    top: `${slotY * cellHeight + margin}px`,
    width: `${Math.max(0, cellWidth - 2 * margin)}px`,
    height: `${Math.max(0, cellHeight - 2 * margin)}px`,
  };
}

export function paintBuilderTrackOverlayCells(overlayEl, metrics) {
  if (!overlayEl || !metrics) return;
  const cols = Math.max(1, Number(metrics.cols) || 1);
  const rows = Math.max(1, Number(metrics.rows) || 1);
  overlayEl.innerHTML = '';
  const frag = document.createDocumentFragment();
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      const cell = document.createElement('div');
      cell.className = 'scorer-layout-builder-track-cell';
      cell.setAttribute('aria-hidden', 'true');
      cell.dataset.trackX = String(x);
      cell.dataset.trackY = String(y);
      Object.assign(cell.style, buildTrackOverlayCellStyle(metrics, x, y));
      frag.appendChild(cell);
    }
  }
  overlayEl.appendChild(frag);
}

export function resolveBuilderGridMetricsFromGrid(grid, profile, profileKey = 'tabletLandscape', scale = 1) {
  const profileGrid = profile?.grid || {};
  const cols = Math.max(1, grid.getColumn?.() || Number(profileGrid.columns) || 12);
  const rows = builderGridRowCount(grid);
  const margin = builderGridMargin(grid);
  const fallback = computeBuilderGridMetrics(profile, profileKey, scale);
  const cellWidth = typeof grid.cellWidth === 'function' ? grid.cellWidth() : fallback.cellWidth;
  const cellHeight = typeof grid.getCellHeight === 'function' ? grid.getCellHeight(true) : fallback.cellHeight;
  const width = grid.el?.clientWidth || fallback.width;
  const height = rows * cellHeight;
  return {
    cols,
    rows,
    margin,
    cellWidth,
    cellHeight,
    width,
    height,
  };
}

/** Repaint dotted overlay from live GridStack cell metrics (call after grid init/resize). */
export function syncBuilderTrackOverlayCells(overlayEl, grid, profile, profileKey = 'tabletLandscape', scale = 1) {
  if (!overlayEl || !grid) return;
  paintBuilderTrackOverlayCells(overlayEl, resolveBuilderGridMetricsFromGrid(grid, profile, profileKey, scale));
}

export function clearBuilderTrackOverlayHighlight(overlayEl) {
  if (!overlayEl) return;
  overlayEl.querySelectorAll('.scorer-layout-builder-track-cell--drop-target').forEach((cell) => {
    cell.classList.remove('scorer-layout-builder-track-cell--drop-target');
  });
}

/** Highlight overlay cells for a grid slot (palette hover or in-grid drag). */
export function paintBuilderTrackOverlayHighlight(overlayEl, grid, x = 0, y = 0, w = 1, h = 1) {
  if (!overlayEl || !grid) return;
  clearBuilderTrackOverlayHighlight(overlayEl);
  const cols = Math.max(1, grid.getColumn?.() || 1);
  const cells = overlayEl.querySelectorAll('.scorer-layout-builder-track-cell');
  const spanW = Math.max(1, Number(w) || 1);
  const spanH = Math.max(1, Number(h) || 1);
  const startX = Math.max(0, Number(x) || 0);
  const startY = Math.max(0, Number(y) || 0);
  for (let dy = 0; dy < spanH; dy += 1) {
    for (let dx = 0; dx < spanW; dx += 1) {
      const idx = (startY + dy) * cols + (startX + dx);
      const target = cells[idx];
      if (target) target.classList.add('scorer-layout-builder-track-cell--drop-target');
    }
  }
}

export function paintBuilderTrackOverlayFromPointer(overlayEl, grid, clientX, clientY, w = 1, h = 1) {
  if (!grid?.el) return null;
  const box = grid.el.getBoundingClientRect();
  if (clientX < box.left || clientX > box.right || clientY < box.top || clientY > box.bottom) {
    clearBuilderTrackOverlayHighlight(overlayEl);
    clearBuilderPalettePlaceholder(grid);
    return null;
  }
  const slot = gridClientPixelToDropSlot(grid, clientX, clientY, w, h);
  paintBuilderTrackOverlayHighlight(overlayEl, grid, slot.x, slot.y, w, h);
  paintBuilderPalettePlaceholder(grid, slot.x, slot.y, w, h);
  return slot;
}

export function findFirstOpenCell(grid, w = 1, h = 1) {
  if (!grid) return null;
  const cols = grid.getColumn();
  const maxRow = Math.max(1, Number(grid.opts?.maxRow) || grid.getRow() || 1);
  for (let y = 0; y < maxRow; y += 1) {
    for (let x = 0; x <= cols - w; x += 1) {
      const node = { x, y, w, h };
      if (grid.willItFit(node)) return { x, y };
    }
  }
  return null;
}

function builderGridRowCount(grid) {
  return Math.max(
    1,
    Number(grid?.el?.getAttribute?.('gs-current-row'))
      || grid?.getRow?.()
      || grid?.opts?.maxRow
      || grid?.opts?.minRow
      || 1,
  );
}

function builderGridMargin(grid, side = 'margin') {
  const opts = grid?.opts || {};
  const fallback = Number(opts.margin) || GRIDSTACK_BUILDER_MARGIN;
  if (side === 'marginLeft') return Number(opts.marginLeft ?? fallback);
  if (side === 'marginTop') return Number(opts.marginTop ?? fallback);
  return fallback;
}

/** Live grid element metrics — must match overlay track + GridStack CSS vars. */
export function resolveBuilderGridMetricsFromElement(grid) {
  if (!grid?.el) return null;
  const box = grid.el.getBoundingClientRect();
  const cols = Math.max(1, grid.getColumn?.() || 1);
  const rows = builderGridRowCount(grid);
  const margin = builderGridMargin(grid);
  if (!box.width || !box.height) return null;
  const cellWidth = typeof grid.cellWidth === 'function' ? grid.cellWidth() : (box.width / cols);
  const cellHeight = typeof grid.getCellHeight === 'function'
    ? grid.getCellHeight(true)
    : (box.height / rows);
  return { box, cols, rows, margin, cellWidth, cellHeight };
}

function pointerAxisToCell(rel, cellSize, maxCells) {
  const idx = Math.floor(Math.max(0, rel) / Math.max(1, cellSize));
  return Math.max(0, Math.min(maxCells - 1, idx));
}

/** Map pointer to top-left grid slot for a w×h widget (margin-aware, matches overlay). */
export function gridClientPixelToDropSlot(grid, clientX, clientY, spanW = 1, spanH = 1) {
  const metrics = resolveBuilderGridMetricsFromElement(grid);
  if (!metrics) return { x: 0, y: 0 };
  const relX = clientX - metrics.box.left;
  const relY = clientY - metrics.box.top;
  const w = Math.max(1, Number(spanW) || 1);
  const h = Math.max(1, Number(spanH) || 1);
  let x = pointerAxisToCell(relX, metrics.cellWidth, metrics.cols);
  let y = pointerAxisToCell(relY, metrics.cellHeight, metrics.rows);
  x = Math.max(0, Math.min(metrics.cols - w, x));
  y = Math.max(0, Math.min(metrics.rows - h, y));
  return { x, y };
}

/** Map pointer to the cell under the cursor (1×1). */
export function gridClientPixelToCell(grid, clientX, clientY) {
  return gridClientPixelToDropSlot(grid, clientX, clientY, 1, 1);
}

/** Find the nearest open slot to a preferred grid position (cursor drop). */
export function findNearestDropSlot(grid, prefX = 0, prefY = 0, w = 1, h = 1) {
  if (!grid) return null;
  const cols = Math.max(1, grid.getColumn?.() || 1);
  const rows = builderGridRowCount(grid);
  const spanW = Math.max(1, Number(w) || 1);
  const spanH = Math.max(1, Number(h) || 1);
  const clamp = (x, y) => ({
    x: Math.max(0, Math.min(cols - spanW, Number(x) || 0)),
    y: Math.max(0, Math.min(rows - spanH, Number(y) || 0)),
  });
  const start = clamp(prefX, prefY);
  const fits = (slot) => grid.willItFit?.({ ...slot, w: spanW, h: spanH });
  if (fits(start)) return start;

  const maxRadius = Math.max(cols, rows);
  for (let radius = 1; radius <= maxRadius; radius += 1) {
    for (let dy = -radius; dy <= radius; dy += 1) {
      for (let dx = -radius; dx <= radius; dx += 1) {
        if (Math.abs(dx) !== radius && Math.abs(dy) !== radius) continue;
        const slot = clamp(start.x + dx, start.y + dy);
        if (fits(slot)) return slot;
      }
    }
  }
  return null;
}

const BUILDER_PALETTE_PLACEHOLDER_CLASS = 'scorer-layout-builder-palette-placeholder';

/** Position an element on the builder grid using GridStack CSS vars. */
export function positionBuilderGridSlotElement(grid, el, x, y, w, h) {
  if (!grid?.el || !el) return;
  const rtl = grid.opts?.rtl === true || (grid.opts?.rtl === 'auto' && grid.el.style.direction === 'rtl');
  const xProp = rtl ? 'right' : 'left';
  const style = el.style;
  style.position = 'absolute';
  style.top = y ? (y === 1 ? 'var(--gs-cell-height)' : `calc(${y} * var(--gs-cell-height))`) : null;
  style[xProp] = x ? (x === 1 ? 'var(--gs-column-width)' : `calc(${x} * var(--gs-column-width))`) : null;
  if (rtl) style.left = null;
  else style.right = null;
  style.width = w > 1 ? `calc(${w} * var(--gs-column-width))` : null;
  style.height = h > 1 ? `calc(${h} * var(--gs-cell-height))` : null;
}

function ensureBuilderPalettePlaceholder(grid) {
  if (!grid?.el) return null;
  let el = grid.el.querySelector(`.${BUILDER_PALETTE_PLACEHOLDER_CLASS}`);
  if (!el) {
    el = document.createElement('div');
    el.className = `grid-stack-item grid-stack-placeholder ${BUILDER_PALETTE_PLACEHOLDER_CLASS}`;
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<div class="placeholder-content"></div>';
    grid.el.appendChild(el);
  }
  return el;
}

export function paintBuilderPalettePlaceholder(grid, x, y, w = 1, h = 1) {
  const el = ensureBuilderPalettePlaceholder(grid);
  if (!el) return;
  positionBuilderGridSlotElement(grid, el, x, y, w, h);
  el.hidden = false;
}

export function clearBuilderPalettePlaceholder(grid) {
  const el = grid?.el?.querySelector?.(`.${BUILDER_PALETTE_PLACEHOLDER_CLASS}`);
  if (el) el.hidden = true;
}

/** Lock builder canvas grid to the device frame so empty grids still show drop cells. */
export function syncBuilderGridCanvas(grid, profile, profileKey = 'tabletLandscape', scale = 1) {
  if (!grid?.el) return;
  const metrics = computeBuilderGridMetrics(profile, profileKey, scale);
  const rows = Math.max(1, Number(profile?.grid?.rows) || grid.opts?.maxRow || grid.opts?.minRow || 8);
  grid.el.style.width = `${metrics.width}px`;
  grid.el.style.height = `${metrics.height}px`;
  grid.el.style.minHeight = `${metrics.height}px`;
  grid.el.setAttribute('gs-current-row', String(rows));
  if (typeof grid.cellHeight === 'function' && grid.opts?.cellHeight) {
    grid.cellHeight(grid.opts.cellHeight);
  }
  if (typeof grid.onResize === 'function') {
    grid.onResize(grid.el.clientWidth);
  }
}

export function profileToGridStackOptions(profile, profileKey = 'tabletLandscape', scale = 1) {
  const grid = profile?.grid || {};
  const columns = Math.max(1, Number(grid.columns) || 12);
  const rows = Math.max(1, Number(grid.rows) || 8);
  const s = Math.max(0.25, Math.min(1, Number(scale) || 1));
  const cellHeight = Math.max(16, Math.floor(computeBuilderCellHeight(profile, profileKey) * s));
  return {
    column: columns,
    margin: GRIDSTACK_BUILDER_MARGIN,
    float: true,
    cellHeight,
    minRow: rows,
    maxRow: rows,
    acceptWidgets: () => true,
    removable: true,
    draggable: {
      handle: '.grid-stack-item-content',
      appendTo: 'body',
      scroll: true,
    },
    resizable: { handles: 'se' },
    animate: true,
    lazyLoad: false,
  };
}

export function widgetToGridStackNode(widget, catalogDef = null) {
  const def = catalogDef || getScorerWidgetDef(widget?.type);
  const { x, y } = schemaToGridStackXY(widget?.col, widget?.row);
  const w = Math.max(def?.minColSpan || 1, Number(widget?.colSpan) || def?.defaultColSpan || 1);
  const h = Math.max(def?.minRowSpan || 1, Number(widget?.rowSpan) || def?.defaultRowSpan || 1);
  return {
    id: String(widget?.id || ''),
    x,
    y,
    w,
    h,
    minW: def?.minColSpan || 1,
    minH: def?.minRowSpan || 1,
    [GRIDSTACK_WIDGET_META_KEY]: { ...widget },
  };
}

export function gridStackNodeToWidget(node, existingWidget = null) {
  const meta = node?.[GRIDSTACK_WIDGET_META_KEY] || existingWidget || {};
  const def = getScorerWidgetDef(meta?.type);
  const { col, row } = gridStackToSchemaXY(node?.x, node?.y);
  const minColSpan = Math.max(1, Number(def?.minColSpan) || 1);
  const minRowSpan = Math.max(1, Number(def?.minRowSpan) || 1);
  return {
    ...meta,
    id: String(node?.id || meta?.id || ''),
    type: String(meta?.type || ''),
    col,
    row,
    colSpan: Math.max(minColSpan, Number(node?.w) || meta?.colSpan || minColSpan),
    rowSpan: Math.max(minRowSpan, Number(node?.h) || meta?.rowSpan || minRowSpan),
    config: { ...(meta?.config || {}) },
  };
}

export function widgetsToGridStackNodes(widgets = []) {
  return (widgets || []).map((widget) => {
    const def = getScorerWidgetDef(widget?.type);
    return widgetToGridStackNode(widget, def);
  });
}

export function syncWidgetsFromGrid(grid, existingWidgets = []) {
  if (!grid?.engine?.nodes) return [];
  const existingById = new Map(
    (existingWidgets || []).map((widget) => [String(widget?.id || ''), widget]),
  );
  return grid.engine.nodes.map((node) => {
    const synced = gridStackNodeToWidget(node, node?.[GRIDSTACK_WIDGET_META_KEY]);
    const prior = existingById.get(String(synced.id || ''));
    if (!prior?.config) return synced;
    return {
      ...synced,
      config: {
        ...(prior.config || {}),
        ...(synced.config || {}),
      },
    };
  });
}

/** Keep GridStack node meta aligned with profile widget data (inspector edits, preview staging). */
export function syncGridWidgetMeta(nodeOrEl, widget) {
  if (!nodeOrEl || !widget) return null;
  const node = nodeOrEl.gridstackNode || nodeOrEl;
  if (!node) return null;
  const snapshot = {
    ...widget,
    config: { ...(widget.config || {}) },
  };
  node[GRIDSTACK_WIDGET_META_KEY] = snapshot;
  if (widget.id) {
    node.id = String(widget.id);
  }
  if (node.el) {
    if (node.id) node.el.setAttribute('gs-id', String(node.id));
    attachWidgetMetaToNode(node.el, snapshot);
  }
  return snapshot;
}

export function paletteItemToGridStackWidget(catalogItem) {
  const def = catalogItem?.paletteKey
    ? getScorerWidgetPaletteDef(catalogItem.paletteKey)
    : getScorerWidgetDef(catalogItem?.type);
  if (!def) return null;
  return {
    w: def.defaultColSpan || 1,
    h: def.defaultRowSpan || 1,
    minW: def.minColSpan || 1,
    minH: def.minRowSpan || 1,
    [GRIDSTACK_WIDGET_META_KEY]: {
      type: def.type,
      paletteKey: def.paletteKey,
      config: { ...(def.defaultConfig || {}) },
    },
  };
}

export function attachWidgetMetaToNode(el, widget) {
  if (!el || !widget) return;
  el.dataset.widgetId = widget.id;
  el.dataset.widgetType = widget.type;
  if (widget.config?.paletteKey) el.dataset.paletteKey = widget.config.paletteKey;
}

export function readPaletteKeyFromNode(node) {
  const meta = node?.[GRIDSTACK_WIDGET_META_KEY];
  if (meta?.paletteKey) return meta.paletteKey;
  if (meta?.config?.paletteKey) return meta.config.paletteKey;
  const type = String(meta?.type || '');
  if (type === 'keypad.digit' && meta?.config?.digit != null) {
    return `keypad.digit.${meta.config.digit}`;
  }
  return getScorerWidgetDef(type)?.paletteKey || type;
}

export function refreshBuilderGridDrag(grid, targetEl = null) {
  if (!grid) return;
  if (typeof grid.enableMove === 'function') {
    grid.enableMove(true);
  }
  if (typeof grid.enableResize === 'function') {
    grid.enableResize(true);
  }
  const nodes = targetEl
    ? [targetEl.gridstackNode || (grid.engine?.nodes || []).find((node) => node.el === targetEl)].filter(Boolean)
    : (grid.engine?.nodes || []);
  nodes.forEach((node) => {
    if (!node?.el) return;
    delete node.noMove;
    node.noMove = false;
    if (typeof grid.prepareDragDrop === 'function') {
      // Chip paint replaces .grid-stack-item-content innerHTML and clears DD bindings.
      delete node._initDD;
      grid.prepareDragDrop(node.el);
    }
  });
  if (!targetEl) {
    if (typeof grid.movable === 'function') {
      grid.movable('.grid-stack-item', true);
    }
    if (typeof grid.refreshDragHandles === 'function') {
      grid.refreshDragHandles('.grid-stack-item');
    }
  }
}

export function loadGridFromProfile(grid, profile, renderContent) {
  if (!grid) return;
  grid.removeAll(false);
  const widgets = profile?.widgets || [];
  widgets.forEach((widget) => {
    const def = getScorerWidgetDef(widget?.type);
    const node = widgetToGridStackNode(widget, def);
    const el = grid.addWidget({ ...node });
    if (!el || typeof renderContent !== 'function') return;
    const chip = renderContent(widget, def);
    if (!chip) return;
    const content = el.querySelector('.grid-stack-item-content');
    if (!content) return;
    content.innerHTML = '';
    content.appendChild(chip);
    syncGridWidgetMeta(el.gridstackNode || el, widget);
  });
  refreshBuilderGridDrag(grid);
}
